// Audio Playback Service - Browser AudioContext Chunk Playback with Barge-in

type AudioPlaybackState = 'idle' | 'playing' | 'stopping';

export type AudioPlaybackEventMap = {
  stateChange: (state: AudioPlaybackState) => void;
  error: (error: Error) => void;
};

type EventKey = keyof AudioPlaybackEventMap;

export class AudioPlaybackService {
  private audioContext: AudioContext | null = null;
  private state: AudioPlaybackState = 'idle';
  private readonly chunkQueue: ArrayBuffer[] = [];
  private isProcessing = false;
  private currentSource: AudioBufferSourceNode | null = null;
  private readonly listeners: { [K in EventKey]: AudioPlaybackEventMap[K][] } = {
    stateChange: [],
    error: [],
  };

  on<K extends EventKey>(event: K, callback: AudioPlaybackEventMap[K]): void {
    this.listeners[event].push(callback);
  }

  off<K extends EventKey>(event: K, callback: AudioPlaybackEventMap[K]): void {
    const idx = this.listeners[event].indexOf(callback);
    if (idx !== -1) {
      this.listeners[event].splice(idx, 1);
    }
  }

  private emit<K extends EventKey>(event: K, ...args: Parameters<AudioPlaybackEventMap[K]>): void {
    for (const listener of this.listeners[event]) {
      (listener as (...a: unknown[]) => void)(...args);
    }
  }

  get currentState(): AudioPlaybackState {
    return this.state;
  }

  get isPlaying(): boolean {
    return this.state === 'playing';
  }

  enqueue(base64Audio: string): void {
    const binary = atob(base64Audio);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    this.chunkQueue.push(bytes.buffer);

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  stop(): void {
    this.setState('stopping');
    this.chunkQueue.length = 0;

    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch {
        // Already stopped
      }
      this.currentSource.disconnect();
      this.currentSource = null;
    }

    this.isProcessing = false;
    this.setState('idle');
  }

  destroy(): void {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }

  private ensureAudioContext(): AudioContext {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext();
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }
    return this.audioContext;
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;

    if (this.state === 'idle' && this.chunkQueue.length > 0) {
      this.setState('playing');
    }

    while (this.chunkQueue.length > 0) {
      if (this.state === 'stopping' || this.state === 'idle') {
        break;
      }

      const chunk = this.chunkQueue.shift();
      if (!chunk) break;

      try {
        await this.playChunk(chunk);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Audio playback failed');
        this.emit('error', error);
      }
    }

    this.isProcessing = false;
    if (this.state === 'playing') {
      this.setState('idle');
    }
  }

  private playChunk(audioData: ArrayBuffer): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const ctx = this.ensureAudioContext();

      ctx.decodeAudioData(
        audioData.slice(0),
        (audioBuffer) => {
          if (this.state !== 'playing') {
            resolve();
            return;
          }

          const source = ctx.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(ctx.destination);
          this.currentSource = source;

          source.onended = () => {
            this.currentSource = null;
            resolve();
          };

          source.start();
        },
        (err) => {
          reject(err instanceof Error ? err : new Error('Failed to decode audio chunk'));
        },
      );
    });
  }

  private setState(newState: AudioPlaybackState): void {
    if (this.state === newState) return;
    this.state = newState;
    this.emit('stateChange', newState);
  }
}
